import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";
import { Clock, CheckCircle2, XCircle, Euro, PlusCircle, ClipboardList, Inbox } from "lucide-react";

const fmtEur = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome" }) : "-";
const PIE_COLORS = ["#002FA7", "#10b981", "#f59e0b", "#e11d48", "#8b5cf6", "#06b6d4"];

function Kpi({ icon: Icon, label, value, tint, testid, onClick }) {
  return (
    <Card
      role="button"
      onClick={onClick}
      data-testid={testid}
      className="cursor-pointer rounded-lg border-slate-200 shadow-sm transition-transform duration-200 hover:-translate-y-[2px] hover:shadow-md"
    >
      <CardContent className="flex items-center gap-4 p-5">
        <div className={`flex h-12 w-12 items-center justify-center rounded-md ${tint}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
          <p className="font-heading text-2xl font-extrabold text-slate-900">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Stats() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  usePolling(() => api.get("/stats").then((r) => setStats(r.data)).catch(() => {}), 15000);

  const isApprover = user?.role === "admin" || user?.role === "approvatore";
  const isUser = user?.role === "utilizzatore";

  const openDetail = async (status, title) => {
    setDetail({ status, title });
    setDetailLoading(true);
    try {
      const params = status === "tutte" ? {} : { status };
      const { data } = await api.get("/requests", { params });
      setDetailItems(data);
    } catch (e) {
      setDetailItems([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const detailColumns = [
    ...(isApprover
      ? [{ key: "employee_name", header: "Dipendente", sortable: true, className: "font-medium text-slate-900", cell: (r) => r.employee_name }]
      : []),
    { key: "created_at", header: "Data", sortable: true, sortValue: (r) => new Date(r.created_at).getTime(), className: "text-slate-500", cell: (r) => fmtDate(r.created_at) },
    { key: "vehicle_name", header: "Mezzo", sortable: true, className: "text-slate-600", cell: (r) => r.vehicle_name },
    { key: "usage", header: "Utilizzo", sortable: true, sortValue: (r) => r.usage_value, className: "text-slate-600", cell: (r) => `${r.usage_value} ${r.usage_type === "km" ? "km" : "ore"}` },
    { key: "fuel_type", header: "Carburante", sortable: true, className: "text-slate-600", cell: (r) => r.fuel_type },
    { key: "amount", header: "Importo", align: "right", sortable: true, className: "text-right font-heading font-bold text-slate-900", cell: (r) => fmtEur(r.amount) },
    { key: "status", header: "Stato", sortable: true, cell: (r) => <StatusBadge status={r.status} /> },
  ];

  const statusData = stats
    ? [
        { name: "In attesa", value: stats.by_status.in_attesa || 0 },
        { name: "Approvate", value: stats.by_status.approvata || 0 },
        { name: "Rifiutate", value: stats.by_status.rifiutata || 0 },
      ]
    : [];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">
            Ciao, {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {isApprover
              ? "Panoramica delle richieste di rifornimento della flotta."
              : "Riepilogo delle tue richieste di rifornimento."}
          </p>
        </div>
        <div className="flex gap-2">
          {isUser && (
            <Link to="/nuova-richiesta">
              <Button className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]" data-testid="stats-new-request-btn">
                <PlusCircle className="mr-2 h-4 w-4" /> Nuova richiesta
              </Button>
            </Link>
          )}
          {isApprover && (
            <Link to="/richieste">
              <Button variant="outline" className="rounded-md" data-testid="stats-review-btn">
                <ClipboardList className="mr-2 h-4 w-4" /> Da approvare
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Clock} label="In attesa" value={stats?.by_status.in_attesa ?? "-"} tint="bg-amber-50 text-amber-600" testid="kpi-pending" onClick={() => openDetail("in_attesa", "Richieste in attesa")} />
        <Kpi icon={CheckCircle2} label="Approvate" value={stats?.by_status.approvata ?? "-"} tint="bg-emerald-50 text-emerald-600" testid="kpi-approved" onClick={() => openDetail("approvata", "Richieste approvate")} />
        <Kpi icon={XCircle} label="Rifiutate" value={stats?.by_status.rifiutata ?? "-"} tint="bg-rose-50 text-rose-600" testid="kpi-rejected" onClick={() => openDetail("rifiutata", "Richieste rifiutate")} />
        <Kpi icon={Euro} label="Autorizzato" value={fmtEur(stats?.amount_approved)} tint="bg-blue-50 text-[#002FA7]" testid="kpi-amount" onClick={() => openDetail("approvata", "Richieste approvate (importo autorizzato)")} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="rounded-lg border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Richieste per stato</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                <YAxis allowDecimals={false} stroke="#64748b" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {statusData.map((_, i) => (
                    <Cell key={i} fill={["#f59e0b", "#10b981", "#e11d48"][i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="rounded-lg border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Importi per carburante</CardTitle>
          </CardHeader>
          <CardContent>
            {stats && stats.fuel_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={stats.fuel_breakdown}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={(e) => e.name}
                  >
                    {stats.fuel_breakdown.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[280px] items-center justify-center text-sm text-slate-400">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl" data-testid="kpi-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{detail?.title}</DialogTitle>
            <DialogDescription>Elenco delle richieste corrispondenti a questo dato.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <p className="py-8 text-center text-sm text-slate-400">Caricamento...</p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <DataTable
                data={detailItems}
                testid="kpi-detail-table"
                rowTestId={(r) => `kpi-detail-row-${r.id}`}
                searchKeys={["employee_name", "vehicle_name", "fuel_type"]}
                searchPlaceholder="Cerca..."
                initialSort={{ key: "created_at", dir: "desc" }}
                emptyIcon={Inbox}
                emptyText="Nessuna richiesta"
                columns={detailColumns}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
